import { useEffect, useRef, useState } from "react";

/**
 * StickyContextBar — 149-bn / 149-bx.
 *
 * Compact page header (lapisan 2 dari 3-tier sticky system). Sticky tepat di
 * bawah AppShell global topbar (top: var(--global-topbar-height)).
 *
 * 149-bx: Scroll-triggered visibility. Bar TIDAK terlihat saat page hero masih
 * di viewport (menghindari duplikasi judul), dan muncul setelah user scroll
 * melewati sentinel. Height selalu di-reserve supaya `--sticky-table-offset`
 * konstan → thead tidak "melompat" saat compact bar toggle.
 *
 * Props:
 *   segments  — array label breadcrumb (ex: ["User Management", "Access Control"])
 *   extra     — konten kanan (context BOD · Lv1 · Super Admin)
 *   testid    — data-testid
 *   className — override tambahan
 *
 * Placement: taruh SETELAH page hero (<h1>...) sebagai sibling. Sentinel
 * otomatis diinjeksi sebagai div di atas bar.
 */
export default function StickyContextBar({ segments = [], extra = null, testid = "sticky-ctx-bar", className = "" }) {
  const sentinelRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    // Bar muncul saat sentinel di atas keluar viewport (page hero scrolled up).
    const io = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { rootMargin: "0px 0px 0px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* Sentinel — 1px tinggi, invisible. Menandai batas "page hero has left". */}
      <div ref={sentinelRef} aria-hidden="true" style={{ height: 1, marginTop: -1 }} />
      <div
        data-testid={testid}
        data-visible={visible ? "true" : "false"}
        className={"ens-ctx-bar px-6 h-8 flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold text-slate-600 -mx-6 " + className}
      >
        <div className="flex items-center gap-1.5 truncate">
          {segments.map((s, i) => (
            <span key={`${s}-${i}`} className="flex items-center gap-1.5 truncate">
              {i > 0 && <span className="text-slate-300">›</span>}
              <span className={i === segments.length - 1 ? "text-[#0B1D3A] truncate" : "text-slate-500 truncate"}>{s}</span>
            </span>
          ))}
        </div>
        {extra && <div className="flex items-center gap-2 text-slate-500 shrink-0">{extra}</div>}
      </div>
    </>
  );
}
